import CardForm from "./cardForm"
import { put } from "../@rails/request.js"

const selector = "[data-billing-omni-form]"

class OmniForm extends CardForm {
  static init() {
    super.init(selector)
  }

  static get submitEvent() {
    if (this._submitEvent === undefined) {
      this._submitEvent = new Event("submit", {
        bubbles: true,
        cancelable: true
      })
      this._submitEvent.billingTaxAddressSet = true
    }
    return this._submitEvent
  }

  constructor(...args) {
    super(...args)
  }

  init() {
    super.init()

    if (!this.dataset.remote) {
      throw new Error(
        "Billing omni-form must have `data-remote` attribute"
      )
    }

    if (this.taxDefaultStrategy) {
      this.addEventListener("submit", (event) => {
        if (event.billingTaxAddressSet) {
          return
        }
        if (this.card.hidden) {
          // Scenarios:
          // - Free plan - customer IP address is used for tax.
          // - Card already exists - card details used for tax.
          return
        }
        event.preventDefault()
        event.stopPropagation()

        this.handleSubmit()
        this.setTaxAddress()
          .then(() => {
            this.submitAfterSetTaxAddress()
          })
          .catch((error) => {}) // noop
      }, { capture: true })

      // Scenario:
      // - Using tax default strategy, but card is hidden.
      // - Skip setting tax address.
      // - Disable form elements (via handleSubmit()) after form data is sent.
      this.addEventListener("ajax:send", () => {
        if (this.card.hidden) {
          this.handleSubmit()
        }
      })

    } else {
      this.addEventListener("ajax:send", () => {
        this.handleSubmit()
      })
    }

    this.addEventListener("ajax:success", (event) => {
      const [data, status, _xhr] = event.detail
      this.response = JSON.parse(data).billing

      this.handleResponse()
    })

    this.addEventListener("ajax:error", () => {
      this.handleError()
    })
  }

  setTaxAddress() {
    return new Promise((resolve, reject) => {
      put("/billing/tax/address", {
        body: {address: this.card.details.address.toObject()},
        contentType: "application/json"
      }).then((response) => {
        if (response.ok) {
          resolve()
        } else if (response.unprocessableEntity) {
          response.json.then((json) => {
            const message = json.error
            this.handleCardError(message)
            reject(message)
          })
        } else {
          // This error is handled in 'catch' below.
          throw new Error(`Billing tax address: invalid server response status ${response.statusCode}`)
        }
      })
      .catch((error) => {
        const message = this.card.wrapper.dataset.billingTaxAddressError
        this.handleCardError(message)
        reject(message)
      })
    })
  }

  // This form submit skips 'setTaxAddress'.
  submitAfterSetTaxAddress() {
    // Problem: disabled form elements won't be submitted to the server.
    // Solution: temporarily enable elements, then disable them again after
    // data is sent.
    this._enableElements()
    this.addEventListener("ajax:send", () => {
      this._disableElements()
    }, { once: true })

    this.dispatchEvent(this.constructor.submitEvent)
  }

  handleResponse() {
    const {
      status,
      resource_path,
      require
    } = this.response

    if (status === "success") {
      this.handleSuccess()
      window.location = resource_path

    } else if (status === "pending") {
      if (require === "payment_method") {
        this.requirePaymentMethod()
      } else if (require === "confirmation") {
        this.requireConfirmation()
      } else {
        throw new Error(`invalid response require value '${require}'`)
      }

    } else {
      throw new Error(`invalid response status '${status}'`)
    }
  }

  requirePaymentMethod() {
    const {
      client_secret,
      verify_path
    } = this.response

    this.card.show()
    this.card.submit(client_secret)
      .then(() => {
        this.handleSuccess()
        window.location = verify_path
      })
      .catch(() => {
        this.handleError()
      })
  }

  requireConfirmation() {
    const {
      client_secret,
      payment_method,
      verify_path
    } = this.response

    this.card.confirm(client_secret, payment_method)
      .then(() => {
        this.handleSuccess()
        window.location = verify_path
      })
      .catch(() => {
        this.handleError()
      })
  }

  get taxDefaultStrategy() {
    return this.card.wrapper.dataset.billingTaxDefaultStrategy
  }

  handleCardError(message) {
    // Function invocation order for the below function matters:
    // 1. handleError() enables all form elements.
    // 2. card.error.set() disables form submit element until error is fixed.
    //
    // If the order is flipped form submit element won't be disabled at the end.
    this.handleError()
    this.card.error.set(message)
  }
}

document.addEventListener("DOMContentLoaded", (event) => OmniForm.init())
